import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

interface DropdownMenuContextType {
  open: boolean;
  setOpen: (o: boolean) => void;
  triggerRef: React.RefObject<HTMLDivElement | null>;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextType>({ open: false, setOpen: () => {}, triggerRef: { current: null } });

function DropdownMenu({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  return (
    <DropdownMenuContext.Provider value={{ open, setOpen, triggerRef }}>
      <div ref={triggerRef} className="relative inline-block text-left">{children}</div>
    </DropdownMenuContext.Provider>
  );
}

function DropdownMenuTrigger({ children, asChild, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }) {
  const { open, setOpen } = React.useContext(DropdownMenuContext);
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ onClick?: () => void }>, { onClick: () => setOpen(!open) });
  }
  return <button onClick={() => setOpen(!open)} {...props}>{children}</button>;
}

function DropdownMenuContent({ className, children, align = "end", side = "bottom", ...props }: React.HTMLAttributes<HTMLDivElement> & { align?: "start" | "end"; side?: "top" | "bottom" }) {
  const { open, setOpen, triggerRef } = React.useContext(DropdownMenuContext);
  const ref = React.useRef<HTMLDivElement>(null);

  // Measure trigger + dropdown, calculate clamped position, then show
  React.useLayoutEffect(() => {
    if (!open || !ref.current || !triggerRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const el = ref.current;
    const elRect = el.getBoundingClientRect();
    const gap = 4;
    const margin = 8;

    let top: number;
    if (side === 'top') {
      top = trigger.top - elRect.height - gap;
    } else {
      top = trigger.bottom + gap;
    }

    let left: number;
    if (align === 'end') {
      left = trigger.right - elRect.width;
    } else {
      left = trigger.left;
    }

    // Clamp to viewport
    left = Math.max(margin, Math.min(left, window.innerWidth - elRect.width - margin));
    top = Math.max(margin, Math.min(top, window.innerHeight - elRect.height - margin));

    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
    el.style.visibility = 'visible';
  });

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, setOpen, triggerRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={ref}
      style={{ position: 'fixed', zIndex: 9999, visibility: 'hidden' }}
      className={cn("min-w-[8rem] rounded-md border bg-popover p-1 text-popover-foreground shadow-md", className)}
      {...props}
    >
      {children}
    </div>,
    document.body
  );
}

function DropdownMenuItem({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { setOpen } = React.useContext(DropdownMenuContext);
  return (
    <div
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground cursor-pointer",
        className
      )}
      onClick={(e) => { props.onClick?.(e); setOpen(false); }}
      {...props}
    />
  );
}

function DropdownMenuSeparator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("-mx-1 my-1 h-px bg-muted", className)} {...props} />;
}

function DropdownMenuLabel({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("px-2 py-1.5 text-sm font-semibold", className)} {...props} />;
}

export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel };
